import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  Input,
  List,
  Spin,
  Empty,
  message,
  Checkbox,
  Space,
  Button,
} from "antd";
import { CopyOutlined } from "@ant-design/icons";
import {
  searchProjects,
  getProject,
  getProjectObjectives,
  getQualificationSteps,
  getProjectPages,
} from "../../services/salesforceProjectApi";
import { fetchProject } from "../../services/projectService";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setIsCreating, setStep } from "../../store/slices/navigationSlice";
import {
  loadCreationData as loadProjectCreationData,
  resetCreation as resetProjectCreation,
  setCreationOriginalData as setProjectOriginalData,
} from "../../store/slices/projectSlice";
import {
  updateCreationObjectives as setCreationObjectives,
  setCreationOriginalData as setObjectivesOriginalData,
} from "../../store/slices/projectObjectiveSlice";
import {
  updateCreationQualificationSteps as setCreationQualSteps,
  setCreationOriginalData as setQualStepsOriginalData,
} from "../../store/slices/qualificationStepSlice";
import {
  updateCreationPages as setCreationPages,
  setCreationOriginalData as setPagesOriginalData,
} from "../../store/slices/pageSlice";
import { usePicklistMap } from "../../hooks/usePicklist";
import { PO_PICKLIST_LABELS } from "../../config/projectObjectiveFields";
import { SOBJECT_PROJECT_OBJECTIVE } from "../../config/sobjectNames";
import { getPreferredDefaultWorkTypeValue } from "../../config/projectObjectiveDefaults.js";

const CLONE_PO_WORK_TYPE_PICKLISTS = [PO_PICKLIST_LABELS.WORK_TYPE];
const EMPTY_PICKLIST = [];

export default function CloneProjectModal({
  visible,
  onClose,
  onSuccess,
  selectedProjectId,
  selectedProjectName,
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const path = window.location.pathname;
  const [search, setSearch] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [cloning, setCloning] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [cloneOptions, setCloneOptions] = useState({
    cloneObjectives: true,
    cloneQualSteps: true,
    clonePages: true,
  });
  const debounceRef = useRef(null);

  const { picklistMap: clonePoPicklistMap } = usePicklistMap(
    CLONE_PO_WORK_TYPE_PICKLISTS,
    SOBJECT_PROJECT_OBJECTIVE,
  );
  const cloneWorkTypePicklist =
    clonePoPicklistMap[PO_PICKLIST_LABELS.WORK_TYPE] ?? EMPTY_PICKLIST;

  // Search projects with debounce
  useEffect(() => {
    if (selectedProjectId) {
      setSelectedProject({ Id: selectedProjectId, Name: selectedProjectName });
    }
    const term = (search || "").trim();
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    if (term.length < 3) {
      setResults([]);
      return;
    }
    setLoading(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await searchProjects(term);
        const projectList = response?.data || response || [];
        setResults(Array.isArray(projectList) ? projectList : []);
      } catch (error) {
        message.error("Failed to search projects");
        console.error(error);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [search, selectedProjectId, selectedProjectName]);

  // Handle project selection
  const handleSelectProject = (project) => {
    setSelectedProject(project);
  };

  // Helper mappers from Salesforce records to Wizard creation shapes
  const mapSfObjectiveToUi = (rec, idx) => {
    const id = rec?.Id || `${Date.now()}-obj-${idx}`;
    return {
      id,
      objectiveName: rec?.Name || "",
      contributorFacingName: rec?.Contributor_Facing_Project_Name__c || "",
      appendToObjectiveName: rec?.Append_to_Objective_Name__c || "",
      dateStart: rec?.Date_Start__c || "",
      dateEnd: rec?.Date_End__c || "",
      selectionCriteria: rec?.Selection_Criteria__c || "",
      targetContributors: rec?.Target_Contributors__c ?? "",
      country: rec?.Country__c || "",
      language: rec?.Language__c || "",
      workType:
        (rec?.Work_Type__c && String(rec.Work_Type__c).trim()) ||
        getPreferredDefaultWorkTypeValue(cloneWorkTypePicklist) ||
        "",
      dialectCountry: rec?.Dialect_Country__c || "",
      languageSkillLevel: rec?.Language_Skill_Level__c || "",
      degreeRequirement: rec?.Degree_Requirement__c || "",
      additionalManualMatchRequired: Boolean(
        rec?.Additional_Manual_Match_Required__c,
      ),
      productivityTargetType: rec?.Productivity_Target_Type__c || "",
      unitPayRate: rec?.Unit_Pay_Rate__c ?? "",
      targetProductionUnits: rec?.Target_Production_Units__c ?? "",
      targetProductionHours: rec?.Target_Production_Hours__c ?? "",
      unitPerHour: rec?.Unit_per_Hour__c ?? "",
      productionPaymentThreshold: rec?.Production_Payment_Threshold__c ?? "",
      qualityPaymentThreshold: rec?.Quality_Payment_Threshold__c ?? "",
      funnelAPercentage: rec?.Funnel_A_Percentage__c ?? 100,
      funnelBPercentage: rec?.Funnel_B_Percentage__c ?? 0,
      funnelCPercentage: rec?.Funnel_C_Percentage__c ?? 0,
      funnelDPercentage: rec?.Funnel_D_Percentage__c ?? 0,
      funnelEPercentage: rec?.Funnel_E_Percentage__c ?? 0,
      minimumRate: rec?.Minimum_Rate__c ?? "",
      maximumRate: rec?.Maximum_Rate__c ?? "",
      projectRate: rec?.Project_Rate__c ?? "",
      projectObjectivePayRate: rec?.Project_Objective_Pay_Rate__c ?? "",
      flatPayRate: rec?.Flat_Pay_Rate__c ?? "",
      projectIncentiveRate: rec?.Project_Incentive_Rate__c ?? "",
      // Default to 5 if not provided by API to satisfy validation
      daysBetweenReminderEmails:
        rec && rec.Days_Between_Reminder_Emails__c !== undefined
          ? rec.Days_Between_Reminder_Emails__c
          : 5,
      emailContentComplete: Boolean(rec?.Email_Content_Complete__c),
      status: rec?.Status__c || "Draft",
      overrideDNH: Boolean(rec?.Override_DNH__c),
      displayLeverPostErrorMessage: Boolean(
        rec?.Display_Lever_Post_Error_Message__c,
      ),
      createInvites: Boolean(rec?.Create_Invites__c),
      countryLocale: rec?.Country_Locale__c || "",
      languageLocale: rec?.Language_Locale__c || "",
      dialectLocale: rec?.Dialect_Locale__c || "",
      refresh: Boolean(rec?.Refresh__c),
      mercuryProjectObjectiveType: rec?.Mercury_Project_Objective_Type__c || "",
      // Optional UI-only fields used in forms
      project: "", // will be bound after project creation
    };
  };

  const mapSfQualStepToUi = (rec, idx, projectName) => {
    const id = rec?.Id || `${Date.now()}-pqs-${idx}`;
    return {
      id,
      name: rec?.Name || "",
      qualificationStep: rec?.Qualification_Step__c || "",
      passingScore: rec?.Passing_Score__c ?? "",
      status: rec?.Project_Qualification_Step_Status__c || "Draft",
      funnel: rec?.Funnel__c || "A",
      stepNumber: rec?.Step_Number__c ?? "",
      numberOfAttempts: rec?.Number_of_Attempts__c ?? "1",
      estimatedTime: rec?.Estimated_Time_to_Complete_mins__c ?? "",
      project: projectName || "",
      // Prefer objective name when available; may be blank if API doesn't expand relationship
      objective:
        rec?.Project_Objective__r && rec.Project_Objective__r.Name
          ? rec.Project_Objective__r.Name
          : "",
    };
  };

  const mapSfPageToUi = (rec, idx) => {
    const id = rec?.Id || `${Date.now()}-pp-${idx}`;
    return {
      id,
      projectPageName: rec?.Name || "",
      projectPageType: rec?.Project_Page_Type__c || "",
      active: rec?.Active__c !== undefined ? Boolean(rec.Active__c) : true,
      projectPageDescription: rec?.Project_Page_Description__c || "",
      // Intentionally DO NOT prefill projectObjectiveId; will be chosen in wizard to avoid mismatched Ids
      projectObjectiveId: "",
    };
  };

  // Handle clone by prefilling the wizard creation state instead of server-side cloning
  const handleClone = async () => {
    if (!selectedProject) {
      message.warning("Please select a project to clone");
      return;
    }

    setCloning(true);
    try {
      const sourceProjectId = selectedProject.Id;

      // 1) Load and map project core data
      const normalized = await fetchProject(sourceProjectId);
      // Also fetch raw project for additional fields not in normalized schema
      let rawProject = null;
      try {
        rawProject = await getProject(sourceProjectId);
      } catch (e) {
        // Safe to continue without raw details
        // eslint-disable-next-line no-console
        console.warn(
          "Could not fetch raw project for extra prefill fields:",
          e,
        );
      }

      // 2) Reset wizard creation state, then load project metadata/settings
      dispatch(resetProjectCreation());
      // Derive additional prefill for fields required by validation/UI but not in normalized mapper
      const rawRecord =
        (Array.isArray(rawProject?.data) && rawProject.data[0]) ||
        rawProject?.data ||
        rawProject ||
        null;
      // Prefer manager email for UserLookupField; fallback to name/id if email not available
      const prefilledManager =
        (rawRecord &&
          rawRecord.Project_Manager__r &&
          rawRecord.Project_Manager__r.Email) ||
        (rawRecord &&
          rawRecord.Project_Manager__r &&
          rawRecord.Project_Manager__r.Name) ||
        (rawRecord && rawRecord.Project_Manager__c) ||
        "";
      // Normalize payment method to UI picklist options
      const mapPaymentMethodToUi = (val) => {
        const v = String(val || "").toLowerCase();
        if (!v) return "";
        if (v.includes("self-reported") && v.includes("productivity"))
          return "Productivity Only and Self Reported Hours";
        if (v.includes("self-reported")) return "Self Reported Hours";
        if (v.includes("productivity")) return "Productivity Only";
        return "";
      };
      const prefilledPaymentRaw =
        (normalized?.info && normalized.info.projectPaymentMethod) ||
        (rawRecord && rawRecord.Project_Payment_Method__c) ||
        "";
      const prefilledPayment = mapPaymentMethodToUi(prefilledPaymentRaw);
      // IMPORTANT: Do not carry over Project ID for Reports to a new project
      const sanitizedMetadata = {
        ...(normalized?.info || {}),
        projectIdForReports: "", // clear unique/reporting id
        projectManager: prefilledManager || "", // for UI validation convenience
        projectPaymentMethod:
          prefilledPayment || normalized?.info?.projectPaymentMethod || "",
        projectPriority: "50",
      };
      const creationPayload = {
        metadata: sanitizedMetadata,
        settings: normalized?.settings || {},
      };
      dispatch(loadProjectCreationData(creationPayload));
      dispatch(setProjectOriginalData(creationPayload));

      // 3) Load optional subcomponents based on user selections
      if (cloneOptions.cloneObjectives) {
        try {
          const objs = await getProjectObjectives(sourceProjectId);
          const list = Array.isArray(objs?.data)
            ? objs.data
            : Array.isArray(objs)
              ? objs
              : [];
          // The pid query returns minimal fields; refetch each objective by Id to get full details
          const ids = list.map((o) => o?.Id).filter(Boolean);
          const detailed = await Promise.all(
            ids.map(async (id) => {
              try {
                const raw = await (
                  await import("../../services/salesforceProjectApi")
                ).getObjective(id);
                // Normalize different response shapes
                const rec =
                  (Array.isArray(raw?.data) && raw.data[0]) ||
                  (Array.isArray(raw) && raw[0]) ||
                  raw?.data ||
                  raw ||
                  null;
                return rec || {};
              } catch (inner) {
                // eslint-disable-next-line no-console
                console.warn(
                  "Failed to fetch objective details for",
                  id,
                  inner,
                );
                return {};
              }
            }),
          );
          const uiObjectives = detailed.map(mapSfObjectiveToUi);
          dispatch(setCreationObjectives(uiObjectives));
          dispatch(setObjectivesOriginalData({ objectives: uiObjectives }));
        } catch (e) {
          console.warn("Failed to prefill objectives:", e);
          dispatch(setCreationObjectives([]));
          dispatch(setObjectivesOriginalData({ objectives: [] }));
        }
      } else {
        dispatch(setCreationObjectives([]));
        dispatch(setObjectivesOriginalData({ objectives: [] }));
      }

      if (cloneOptions.cloneQualSteps) {
        try {
          const steps = await getQualificationSteps(sourceProjectId);
          const list = Array.isArray(steps?.data)
            ? steps.data
            : Array.isArray(steps)
              ? steps
              : [];
          const projectDisplay =
            normalized?.info?.name ||
            normalized?.info?.shortName ||
            normalized?.info?.contributorProjectName ||
            "";
          const uiSteps = list.map((rec, idx) =>
            mapSfQualStepToUi(rec, idx, projectDisplay),
          );
          dispatch(setCreationQualSteps(uiSteps));
          dispatch(setQualStepsOriginalData({ qualificationSteps: uiSteps }));
        } catch (e) {
          console.warn("Failed to prefill qualification steps:", e);
          dispatch(setCreationQualSteps([]));
          dispatch(setQualStepsOriginalData({ qualificationSteps: [] }));
        }
      } else {
        dispatch(setCreationQualSteps([]));
        dispatch(setQualStepsOriginalData({ qualificationSteps: [] }));
      }

      if (cloneOptions.clonePages) {
        try {
          const pages = await getProjectPages(sourceProjectId);
          const list = Array.isArray(pages?.data)
            ? pages.data
            : Array.isArray(pages)
              ? pages
              : [];
          const uiPages = list.map(mapSfPageToUi);
          dispatch(setCreationPages(uiPages));
          dispatch(
            setPagesOriginalData({
              pages: uiPages,
              pageComponents: [],
              images: [],
            }),
          );
        } catch (e) {
          console.warn("Failed to prefill project pages:", e);
          dispatch(setCreationPages([]));
          dispatch(
            setPagesOriginalData({ pages: [], pageComponents: [], images: [] }),
          );
        }
      } else {
        dispatch(setCreationPages([]));
        dispatch(
          setPagesOriginalData({ pages: [], pageComponents: [], images: [] }),
        );
      }

      // 4) Open wizard in creation mode at Step 1 with prefilled data
      dispatch(setIsCreating(true));
      dispatch(setStep(1));

      // Close modal and navigate to wizard
      onClose && onClose();
      navigate("/create");

      message.success(
        "Wizard prefilled from selected project. Review and Save to create.",
      );

      if (onSuccess) {
        onSuccess({
          id: normalized?.id,
          metadata: normalized?.info,
        });
      }
    } catch (error) {
      message.error(
        "Failed to prefill from project: " +
          (error.response?.data?.error?.message || error.message),
      );
      console.error("Prefill error:", error);
    } finally {
      setCloning(false);
    }
  };

  const handleCancel = () => {
    setSearch("");
    setResults([]);
    setSelectedProject(null);
    onClose();
  };

  return (
    <Modal
      title={
        <Space>
          <CopyOutlined />
          <span>Clone Project</span>
        </Space>
      }
      open={visible}
      onCancel={handleCancel}
      onOk={handleClone}
      okText="Clone Project"
      okButtonProps={{
        disabled: !selectedProject || cloning,
        loading: cloning,
      }}
      width={700}
    >
      <div className="space-y-4">
        {/* Search Input */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            {selectedProjectName
              ? "Selected Project :"
              : "Search for a project to clone :"}
          </label>
          <Input.Search
            placeholder="Search projects by name..."
            value={selectedProjectName || search}
            onChange={(e) => setSearch(e.target.value)}
            allowClear
            size="large"
            loading={loading}
          />
        </div>

        {/* Project Results */}
        {!selectedProjectName && (
          <div
            style={{
              maxHeight: "300px",
              overflowY: "auto",
              border: "1px solid #f0f0f0",
              borderRadius: "4px",
            }}
          >
            {loading ? (
              <div className="text-center py-8">
                <Spin tip="Searching projects..." />
              </div>
            ) : results.length > 0 ? (
              <List
                dataSource={results}
                renderItem={(item) => (
                  <List.Item
                    className={`cursor-pointer transition-colors px-4 ${
                      selectedProject?.Id === item.Id
                        ? "bg-blue-50"
                        : "hover:bg-gray-50"
                    }`}
                    onClick={() => handleSelectProject(item)}
                    style={{ cursor: "pointer" }}
                  >
                    <List.Item.Meta
                      title={
                        <div className="flex items-center">
                          {selectedProject?.Id === item.Id && (
                            <CopyOutlined className="mr-2 text-blue-500" />
                          )}
                          <span className="font-medium">{item.Name}</span>
                        </div>
                      }
                      description={
                        <div className="text-sm text-gray-500">
                          <div>Status: {item.Project_Status__c || "N/A"}</div>
                          {item.Short_Project_Name__c && (
                            <div>Short Name: {item.Short_Project_Name__c}</div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : search.length >= 3 ? (
              <Empty description="No projects found" className="py-8" />
            ) : (
              <Empty
                description="Type at least 3 characters to search"
                className="py-8"
              />
            )}
          </div>
        )}

        {/* Clone Options */}
        {selectedProject && (
          <div className="border-t pt-4">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Clone Options:
            </label>
            <Space direction="vertical" size="middle">
              <Checkbox
                checked={cloneOptions.cloneObjectives}
                onChange={(e) =>
                  setCloneOptions({
                    ...cloneOptions,
                    cloneObjectives: e.target.checked,
                  })
                }
              >
                <span className="font-medium">Include Project Objectives</span>
                <div className="text-xs text-gray-500 ml-6">
                  Clone all project objectives associated with this project
                </div>
              </Checkbox>
              <Checkbox
                checked={cloneOptions.cloneQualSteps}
                onChange={(e) =>
                  setCloneOptions({
                    ...cloneOptions,
                    cloneQualSteps: e.target.checked,
                  })
                }
              >
                <span className="font-medium">Include Qualification Steps</span>
                <div className="text-xs text-gray-500 ml-6">
                  Clone all qualification steps associated with this project
                </div>
              </Checkbox>
              <Checkbox
                checked={cloneOptions.clonePages}
                onChange={(e) =>
                  setCloneOptions({
                    ...cloneOptions,
                    clonePages: e.target.checked,
                  })
                }
              >
                <span className="font-medium">Include Project Pages</span>
                <div className="text-xs text-gray-500 ml-6">
                  Clone all project pages associated with this project
                </div>
              </Checkbox>
            </Space>
          </div>
        )}

        {/* Selected Project Info */}
        {selectedProject && (
          <div className="bg-blue-50 p-3 rounded-md border border-blue-200">
            <div className="text-sm">
              <div className="font-medium text-blue-900">
                Selected: {selectedProject.Name}
              </div>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
